import { type MaskData, VisionProjectType } from '@/models/RobVision';
import gettext from '@/i18n/gettext';

// Partial: the backend may send a project type this frontend doesn't know
// yet — those fall back to a neutral icon / the raw type string.
export function projectTypeIcon(type: VisionProjectType): string {
  const icons: Partial<Record<VisionProjectType, string>> = {
    [VisionProjectType.CADPicking]: 'mdi-cube-outline',
    [VisionProjectType.LabelPicking]: 'mdi-image-filter-center-focus',
    [VisionProjectType.Depalletizing]: 'mdi-view-grid-outline',
  };
  return icons[type] ?? 'mdi-shape-outline';
}

export function projectTypeLabel(type: VisionProjectType): string {
  const { $gettext } = gettext;
  const labels: Partial<Record<VisionProjectType, string>> = {
    [VisionProjectType.CADPicking]: $gettext('CAD Picking'),
    [VisionProjectType.LabelPicking]: $gettext('Label Picking'),
    [VisionProjectType.Depalletizing]: $gettext('Depalletizing'),
  };
  return labels[type] ?? type;
}

// Nearest-neighbour resample. Despite the historical "upscale" naming, this
// works for both up- and down-sampling — the dst dims may be smaller than
// the source.
export function resampleMaskNearest(
  src: Uint8Array,
  srcW: number,
  srcH: number,
  dstW: number,
  dstH: number,
): Uint8Array {
  if (srcW === dstW && srcH === dstH) {
    return new Uint8Array(src);
  }
  const dst = new Uint8Array(dstW * dstH);
  const xRatio = srcW / dstW;
  const yRatio = srcH / dstH;
  for (let y = 0; y < dstH; y++) {
    const sy = Math.min(srcH - 1, Math.floor(y * yRatio));
    const srcRow = sy * srcW;
    const dstRow = y * dstW;
    for (let x = 0; x < dstW; x++) {
      const sx = Math.min(srcW - 1, Math.floor(x * xRatio));
      dst[dstRow + x] = src[srcRow + sx];
    }
  }
  return dst;
}

export function resampleMaskTo(
  mask: MaskData,
  dstWidth: number,
  dstHeight: number,
): MaskData {
  return {
    data: resampleMaskNearest(
      mask.data,
      mask.width,
      mask.height,
      dstWidth,
      dstHeight,
    ),
    width: dstWidth,
    height: dstHeight,
  };
}

export function computeBoundingBox(
  mask: MaskData,
): { x: number; y: number; w: number; h: number } | null {
  const { data, width, height } = mask;
  let minX = width;
  let maxX = 0;
  let minY = height;
  let maxY = 0;
  let found = false;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (data[i] > 0) {
        if (x < minX) {
          minX = x;
        }
        if (x > maxX) {
          maxX = x;
        }
        if (y < minY) {
          minY = y;
        }
        if (y > maxY) {
          maxY = y;
        }
        found = true;
      }
    }
  }

  if (!found) {
    return null;
  }
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

export async function cropDataURLByMask(
  cutoutUrl: string,
  mask: MaskData,
): Promise<string | null> {
  const bbox = computeBoundingBox(mask);
  if (!bbox) {
    return null;
  }

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = cutoutUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = bbox.w;
  canvas.height = bbox.h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D context not supported or canvas is null');
  }
  ctx.drawImage(
    img,
    bbox.x,
    bbox.y,
    bbox.w,
    bbox.h, // source
    0,
    0,
    bbox.w,
    bbox.h, // dest
  );
  return canvas.toDataURL('image/png');
}
